import type { PortalPromoDto } from '@supply-mover-2-0/shared';

/**
 * Where a promo tile click should lead, expressed as an intent rather
 * than a URL.
 *
 * The tile is rendered in two applications with different routers — the
 * storefront navigates, the CRM storefront designer must not — so the
 * shared component resolves the promo's CTA fields into one of these
 * and hands it to its caller. Each application decides what to do with
 * it. Keeping the resolution here means both surfaces agree on what a
 * given CTA configuration means, and the mapping stays unit-testable
 * without a router.
 */
export type PromoClickTarget = { promoId: number } & (
	| { kind: 'promo-detail' }
	| { kind: 'category'; category: string }
	| { kind: 'item'; itemId: string }
	| { kind: 'item-group'; itemGroup: string }
	| { kind: 'external-url'; url: string }
);

/**
 * Resolve a promo's CTA configuration to a click target.
 *
 * Returns `null` when the promo is not actionable — which happens only
 * when an explicit non-NONE CTA type has no value, i.e. the promo is
 * misconfigured. `NONE` (the column default) is always actionable and
 * falls through to the built-in detail page, so an admin never has to
 * fill in CTA fields just to make a promo clickable.
 */
export function resolvePromoClickTarget(
	promo: PortalPromoDto,
): PromoClickTarget | null {
	const ctaValue = promo.ctaLinkValue;
	// Carried on every variant, not just the detail one: a category or
	// item-group destination still needs to know which promo sent the
	// customer there so the catalog can stay filtered to it.
	const promoId = promo.id;

	if (promo.ctaLinkType === 'NONE') {
		return { kind: 'promo-detail', promoId };
	}

	if (ctaValue == null || ctaValue === '') {
		return null;
	}

	switch (promo.ctaLinkType) {
		case 'CATEGORY':
			return { kind: 'category', category: ctaValue, promoId };
		case 'ITEM':
			return { kind: 'item', itemId: ctaValue, promoId };
		case 'ITEM_GROUP':
			return { kind: 'item-group', itemGroup: ctaValue, promoId };
		case 'EXTERNAL_URL':
			return { kind: 'external-url', url: ctaValue, promoId };
		default:
			return null;
	}
}
